import { useEffect, useState, useMemo } from 'react'
import { User, X } from 'lucide-react'
import { ChatWidgetRoot, createWidgetSocket } from '@kc3/chat-widget'
import '@kc3/chat-widget/embed.css'
import { clientApi } from '../../api/client-portal.js'
import { useClientAuthStore } from '../../store/clientAuthStore.js'
import { loadGuestIdentity, saveGuestIdentity, type GuestIdentity } from '../../utils/clientGuestIdentity.js'
import { ClientAiChat } from './ClientAiChat.js'
import { ClientCallWidget } from './ClientCallWidget.js'
import { GuestIdentityForm } from './GuestIdentityForm.js'
import { PORTAL_HANDOFF_EVENT } from './voice/ClientVoiceAssistant.js'

type Mode = 'closed' | 'identity' | 'ai' | 'live' | 'call'

interface ClientChatLauncherProps {
  tenantSlug: string
}

export function ClientChatLauncher({ tenantSlug }: ClientChatLauncherProps) {
  const [mode, setMode] = useState<Mode>('closed')
  const [liveSession, setLiveSession] = useState<{ token: string; visitorId: string; chatId?: string; reassigned?: boolean } | null>(null)
  const [liveLoading, setLiveLoading] = useState(false)
  const [callEnabled, setCallEnabled] = useState(false)
  const [ratingEnabled, setRatingEnabled] = useState(false)
  const [ratingScale, setRatingScale] = useState(5)
  // PV-16: tenant privacy notice (null when the tenant has not enabled it).
  const [privacyNotice, setPrivacyNotice] = useState<{ url?: string; text?: string; version: string } | null>(null)
  const loggedIn = useClientAuthStore((s) => !!s.user)
  const [guestIdentity, setGuestIdentity] = useState<GuestIdentity | null>(() => loadGuestIdentity(tenantSlug))
  const [targetMode, setTargetMode] = useState<'live' | 'call'>('live')

  const openLive = () => {
    setLiveSession(null)
    if (!loggedIn && !guestIdentity) {
      console.log('[kc3-launcher] Guest identity missing for unauthenticated user — prompting GuestIdentityForm before live escalation')
      setTargetMode('live')
      setMode('identity')
      return
    }
    setMode('live')
  }

  const restartLive = () => {
    console.log('[kc3-launcher] restarting live chat — clearing session')
    if (liveSession?.visitorId) {
      try {
        localStorage.removeItem(`kc3-widget-conversation:${liveSession.visitorId}`)
      } catch {
        // ignore storage errors
      }
    }
    setLiveSession(null)
  }

  const closeLive = () => {
    setLiveSession(null)
    setMode('ai')
  }

  const openCall = () => {
    if (!loggedIn && !guestIdentity) {
      setTargetMode('call')
      setMode('identity')
      return
    }
    setMode('call')
  }

  // The AI voice assistant dials the hand-off queue itself; this event is its
  // "continue in chat" option (e.g. nobody answered), so open live chat.
  useEffect(() => {
    const onHandoff = (): void => { openLive() }
    window.addEventListener(PORTAL_HANDOFF_EVENT, onHandoff)
    return () => window.removeEventListener(PORTAL_HANDOFF_EVENT, onHandoff)
  })

  const handleIdentitySubmit = (identity: GuestIdentity) => {
    saveGuestIdentity(tenantSlug, identity)
    setGuestIdentity(identity)
    setMode(targetMode)
  }

  useEffect(() => {
    let cancelled = false
    clientApi
      .get<{
        config: {
          accessPermissions: { call: { enabled: boolean } }
          rating: { perInteractionEnabled: boolean; ratingConfig: { scale: number } }
          privacyNotice?: { enabled: boolean; noticeUrl: string; noticeText: string; version: string }
        }
      }>(`/portal-config/live?tenantSlug=${tenantSlug}`)
      .then((res) => {
        if (!cancelled) {
          setCallEnabled(res.config.accessPermissions.call.enabled)
          setRatingEnabled(res.config.rating.perInteractionEnabled)
          setRatingScale(res.config.rating.ratingConfig.scale)
          const n = res.config.privacyNotice
          setPrivacyNotice(
            n && n.enabled && n.version && (n.noticeUrl || n.noticeText)
              ? { url: n.noticeUrl, text: n.noticeText, version: n.version }
              : null,
          )
        }
      })
      .catch(() => {
        if (!cancelled) setCallEnabled(false)
      })
    return () => {
      cancelled = true
    }
  }, [tenantSlug])

  useEffect(() => {
    if (mode !== 'live' || liveSession) return
    let cancelled = false
    setLiveLoading(true)
    console.log('[kc3-launcher] ➔ STEP 1: "Live Agent" option active — requesting chat session via POST /portal/' + tenantSlug + '/chat/session...')
    clientApi
      .post<{ token: string; visitorId: string; chatId?: string; reassigned?: boolean }>(
        `/portal/${tenantSlug}/chat/session`,
        !loggedIn && guestIdentity ? { name: guestIdentity.name, email: guestIdentity.email } : {},
      )
      .then((res) => {
        if (!cancelled) {
          console.log('[kc3-launcher] ✔ STEP 2: Chat session successfully created! visitorId:', res.visitorId, 'chatId:', res.chatId ?? 'none', 'token:', res.token ? 'YES' : 'NO')
          if (res.chatId && !loggedIn) {
            // Anonymous/guest visitors: persist the conversation mapping in
            // localStorage so a page refresh restores the thread. Logged-in
            // clients get the chatId via initialConversationId prop instead —
            // the database is the source of truth, not the browser.
            localStorage.setItem(`kc3-widget-conversation:${res.visitorId}`, res.chatId)
          } else if (!res.chatId && !loggedIn) {
            // No existing chat to reuse (new visitor, or the previous
            // agent was unavailable and the old chat was abandoned).
            // Clear any stale conversation mapping so the widget starts
            // a fresh escalation instead of routing into a dead thread.
            try {
              localStorage.removeItem(`kc3-widget-conversation:${res.visitorId}`)
            } catch {
              // ignore storage errors
            }
          }
          setLiveSession(res)

          if (res.token) {
            clientApi
              .get<{ config?: { privacyNotice?: { enabled?: boolean; version?: string } } }>(
                `/portal-config/live?tenantSlug=${tenantSlug}`,
              )
              .then((cfgRes) => {
                const notice = cfgRes.config?.privacyNotice
                if (notice?.enabled && notice.version) {
                  fetch('/widget/v1/privacy-acceptance', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${res.token}`,
                    },
                    body: JSON.stringify({
                      version: notice.version,
                      channel: 'portal_chat',
                    }),
                  }).catch(() => {
                    // best-effort
                  })
                }
              })
              .catch(() => {
                // ignore
              })
          }
        }
      })
      .catch((err) => {
        console.error('[kc3-launcher] ❌ ERROR: Chat session request failed:', err)
        if (!cancelled) setMode('ai')
      })
      .finally(() => {
        if (!cancelled) setLiveLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [mode, liveSession, tenantSlug, loggedIn, guestIdentity])

  useEffect(() => {
    if (mode !== 'live' || !liveSession) return
    console.log('[kc3-launcher] live session ready — dispatching kc3:open-chat event')
    window.dispatchEvent(new CustomEvent('kc3:open-chat'))
  }, [mode, liveSession])

  const socket = useMemo(() => {
    if (!liveSession) return null
    console.log('[kc3-launcher] ➔ STEP 3: Initializing Socket.IO connection to gateway...')
    const s = createWidgetSocket(liveSession.token)
    s.on('connect', () => {
      console.log('[kc3-launcher] ✔ STEP 4: Socket connected successfully! socket.id:', s.id)
    })
    s.on('connect_error', (err) => {
      console.error('[kc3-launcher] ❌ ERROR: Socket connection failed:', err)
    })
    return s
  }, [liveSession?.token])

  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect()
      }
    }
  }, [socket])

  if (mode === 'closed') {
    return (
      <button type="button" className="kc3-widget-bubble" onClick={() => setMode('ai')} aria-label="Open support">
        <span className="kc3-widget-bubble__icon" aria-hidden="true">💬</span>
      </button>
    )
  }

  if (mode === 'identity') {
    return (
      <GuestIdentityForm
        tenantSlug={tenantSlug}
        onSubmit={handleIdentitySubmit}
        onClose={() => setMode('ai')}
      />
    )
  }

  if (mode === 'ai') {
    return (
      <ClientAiChat
        tenantSlug={tenantSlug}
        onClose={() => setMode('closed')}
        onTalkToHuman={openLive}
        callEnabled={callEnabled}
        onCallSupport={openCall}
      />
    )
  }

  if (mode === 'call') {
    return (
      <ClientCallWidget
        tenantSlug={tenantSlug}
        perInteractionEnabled={ratingEnabled}
        ratingScale={ratingScale}
        onClose={() => setMode('ai')}
        onContinueChat={() => setMode('live')}
      />
    )
  }

  if (liveLoading || !liveSession || !socket) {
    return (
      <div className="kc3-widget-panel">
        <button type="button" className="kc3-widget-close" onClick={() => setMode('ai')} aria-label="Close">
          <X className="h-3 w-3" />
        </button>
        <div className="kc3-widget h-full overflow-hidden flex flex-col">
          <div className="flex items-center gap-3 px-5 py-5 bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex-shrink-0">
            <div className="h-11 w-11 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <User className="h-6 w-6" />
            </div>
            <div>
              <p className="font-semibold text-base leading-tight">Live Agent</p>
              <p className="text-sm text-white/80">Connecting you to a support agent</p>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center text-sm text-gray-600">Connecting to an agent…</div>
        </div>
      </div>
    )
  }

  return (
    <ChatWidgetRoot
      key={liveSession.visitorId}
      socket={socket}
      visitorId={liveSession.visitorId}
      token={liveSession.token}
      onClose={closeLive}
      onRestart={restartLive}
      showRating={ratingEnabled}
      ratingScale={ratingScale}
      tenantSlug={tenantSlug}
      initialOption="__talk_to_agent__"
      {...(privacyNotice && { privacyNotice })}
      {...(liveSession.chatId && { initialConversationId: liveSession.chatId })}
      persistHistory={!loggedIn}
      {...(liveSession.reassigned === true && { initialNotice: 'Your previous agent is no longer available. Connecting you with a new available agent…' })}
    />
  )
}

